import { ReactElement, useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import GratiPageWrapper from '../components/common/GratiPageWrapper';
import USGALoginControl from '../components/usga/USGALoginControl';
import { useApp } from '../contextProviders/AppProvider';
import { useAuth } from '../contextProviders/AuthProvider';
import { useUSGA } from '../contextProviders/USGAProvider';
import { useGhinStagingMutators } from '../dataMutators/useGhinStagingMutators';
import Person from '../types/Person';
// import { coursesSearch } from '../usga/apis/coursesSearch';
import { golfersHandicapHistory } from '../usga/apis/golfersHandicapHistory';
import { golfersSearch, GolferSearchResult } from '../usga/apis/golfersSearch';

export default function USGATestPage(): ReactElement {
  const { isGod } = useAuth();
  const { isUSGALoggedIn, usgaLogout } = useUSGA();
  const { setPageName } = useApp();
  const { triggerUpdateNow } = useGhinStagingMutators();

  const [response, setResponse] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [ghin, setGhin] = useState('');
  const [isFetchingGhin, setIsFetchingGhin] = useState(false);

  // const [courseSearch, setCourseSearch] = useState('');
  // const [isFetchingCourses, setIsFetchingCourses] = useState(false);

  const [golferSearchGhin, setGolferSearchGhin] = useState('');
  const [golferStatusFilter, setGolferStatusFilter] = useState<'Active' | 'Inactive' | 'All'>('Active');
  const [isFetchingGolfers, setIsFetchingGolfers] = useState(false);
  const [golferResults, setGolferResults] = useState<GolferSearchResult[]>([]);

  const [isTriggering, setIsTriggering] = useState(false);

  useEffect(() => {
    setPageName('USGA Test');
  }, [setPageName]);

  const validateUser = (userProfile: Person | null | undefined) => {
    return userProfile && isGod ? true : false;
  };

  const handleFetchGhin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsFetchingGhin(true);
    setResponse(null);
    setError(null);

    try {
      const result = await golfersHandicapHistory(ghin);
      setResponse(JSON.stringify(result, null, 2));
      if (!result.success) {
        if (result.status === 401) usgaLogout();
        setError(result.error || 'Failed to fetch GHIN');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsFetchingGhin(false);
    }
  };

  // const handleCourseSearch = async (e: React.FormEvent<HTMLFormElement>) => { ... };

  const handleGolferSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsFetchingGolfers(true);
    setGolferResults([]);
    setResponse(null);
    setError(null);

    try {
      const result = await golfersSearch(golferSearchGhin, golferStatusFilter === 'All' ? undefined : golferStatusFilter);
      if (result.success && result.data) {
        setGolferResults(result.data.golfers);
      } else {
        if (result.status === 401) usgaLogout();
        setError(result.error || 'Failed to search golfers');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsFetchingGolfers(false);
    }
  };

  const isValidGhin = ghin.length > 0 && ghin.length <= 9 && /^\d+$/.test(ghin);

  const handleGetNow = async () => {
    setIsTriggering(true);
    setError(null);
    try {
      const year = new Date().getFullYear().toString();
      await triggerUpdateNow(year);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger fetch');
    } finally {
      setIsTriggering(false);
    }
  };

  return (
    <GratiPageWrapper validateUser={validateUser}>
      <Stack spacing={2} sx={{ maxWidth: 'md' }}>
        <Typography variant="headlineSmall">GHIN Staging Test</Typography>
        <Typography variant="bodyMedium" color="text.secondary">
          Test the GHIN2020 staging API.
        </Typography>

        <USGALoginControl />

        {isUSGALoggedIn && (
          <Stack spacing={3}>
            <Stack spacing={2} component="form" onSubmit={handleFetchGhin}>
              <Typography variant="titleMedium">Fetch GHIN Handicap History</Typography>
              <TextField
                required
                id="ghin"
                label="GHIN"
                value={ghin}
                onChange={(e) => setGhin(e.target.value.replace(/\D/g, '').slice(0, 9))}
                slotProps={{ htmlInput: { maxLength: 9, inputMode: 'numeric' } }}
                helperText="Enter a GHIN number (up to 9 digits)"
              />
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isFetchingGhin || !isValidGhin}
                >
                  {isFetchingGhin ? <CircularProgress size={24} /> : 'Fetch'}
                </Button>
              </Stack>
            </Stack>

            <Stack spacing={2} component="form" onSubmit={handleGolferSearch}>
              <Typography variant="titleMedium">Search Golfers</Typography>
              <TextField
                required
                id="golferSearchGhin"
                label="GHIN(s)"
                value={golferSearchGhin}
                onChange={(e) => setGolferSearchGhin(e.target.value.replace(/[^\d,]/g, ''))}
                helperText="Enter one or more GHIN numbers separated by commas"
              />
              <ToggleButtonGroup
                value={golferStatusFilter}
                exclusive
                onChange={(_, val) => { if (val) setGolferStatusFilter(val); }}
                size="small"
              >
                <ToggleButton value="Active">Active</ToggleButton>
                <ToggleButton value="Inactive">Inactive</ToggleButton>
                <ToggleButton value="All">All</ToggleButton>
              </ToggleButtonGroup>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={isFetchingGolfers || golferSearchGhin.length === 0}
                >
                  {isFetchingGolfers ? <CircularProgress size={24} /> : 'Search'}
                </Button>
              </Stack>
              {golferResults.length > 0 && (
                <Stack spacing={1}>
                  {golferResults.map((g) => (
                    <Typography key={`${g.ghin}-${g.club_id}`} variant="bodySmall">
                      {g.ghin} — {g.first_name} {g.last_name} · {g.club_name} ({g.status}) · HI: {g.hi_display ?? 'N/A'}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>

            {/* Search Courses — commented out pending course integration */}

            <Stack spacing={2}>
              <Typography variant="titleMedium">Fetch Daily GHIN Indices</Typography>
              <Button
                variant="contained"
                onClick={handleGetNow}
                disabled={isTriggering}
              >
                {isTriggering ? <CircularProgress size={24} /> : 'Get Now'}
              </Button>
            </Stack>
          </Stack>
        )}

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        {response && (
          <Box>
            <Typography variant="labelLarge" gutterBottom>Response:</Typography>
            <Box component="pre">
              {response}
            </Box>
          </Box>
        )}
      </Stack>
    </GratiPageWrapper>
  );
}
